import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import { Operator, PaginatedResponse } from "@/api.types";
import * as z from "zod";
import { createOperatorSchema } from "@/api.schemas";
import { useEffect, useState } from "react";
import socketService, { HeartbeatData } from "@/services/socket.io/socket";

export type CreateOperatorSchema = z.infer<typeof createOperatorSchema>;

// Add these new types at the top with other interfaces
interface PaginationParams {
  page?: number;
  limit?: number;
  paginationType?: "offset" | "cursor";
  cursor?: string;
}

interface FilterParams {
  status?: string; // Changed to string to support comma-separated values like "ON_DUTY,OFF_DUTY"
  search?: string;
  sortBy?: "createdAt" | "updatedAt";
  sortOrder?: "asc" | "desc";
}

// Combine both types
type OperatorsQueryParams = PaginationParams & FilterParams;

// Add this utility function
const buildPaginationParams = (
  params: PaginationParams & Record<string, any>
): URLSearchParams => {
  const queryParams = new URLSearchParams();

  // Handle pagination params
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.paginationType)
    queryParams.append("paginationType", params.paginationType);
  if (params.cursor) queryParams.append("cursor", params.cursor);

  // Handle any additional filter params
  Object.entries(params).forEach(([key, value]) => {
    if (
      value !== undefined &&
      !["page", "limit", "paginationType", "cursor"].includes(key)
    ) {
      queryParams.append(key, String(value));
    }
  });

  return queryParams;
};

// Update useOperators to use the new utility
export function useOperators(params: OperatorsQueryParams = {}) {
  const { token } = useSession();
  const queryParams = buildPaginationParams(params);

  return useQuery<PaginatedResponse<Operator>>({
    queryKey: ["operators", params],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<Operator>>(
        `${import.meta.env.VITE_API_URL}/api/v1/operators?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Create operator mutation
export function useCreateOperator({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<Operator, Error, CreateOperatorSchema>({
    mutationFn: async (values) => {
      const { depotId, authMethod, ...operatorData } = values;
      const { data } = await axios.post<Operator>(
        `${import.meta.env.VITE_API_URL}/api/v1/depots/${depotId}/operators/create`,
        { ...operatorData, authMethod },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["operators"] });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

// Real-time operator updates using socket.io
export function useSocketOperators(params: OperatorsQueryParams = {}) {
  const { token } = useSession();
  const queryParams = buildPaginationParams(params);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [operatorIds, setOperatorIds] = useState<string[]>([]);

  useQuery({
    queryKey: ["operators", token, queryParams],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<Operator>>(
        `${import.meta.env.VITE_API_URL}/api/v1/operators?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setOperators(data.data);
      setOperatorIds(data.data.map((op) => op.id));
      return data;
    },
  });

  useEffect(() => {
    if (operatorIds) {
      operatorIds.forEach((id) => {
        socketService.subscribeToOperator(id);
      });
    }
    return () => {
      operatorIds.forEach((id) => {
        socketService.unsubscribeFromOperator(id);
      });
    };
  }, [operatorIds]);

  useEffect(() => {
    // Set up heartbeat event listener
    const handleHeartbeat = (data: HeartbeatData) => {
      console.log("Heartbeat received:", data);
      setOperators((prev: Operator[]) =>
        prev.map((op: Operator) =>
          op.id === data.operatorId
            ? {
                ...op,
                // Update location only if provided in the heartbeat
                ...(data.location && {
                  lastLocation: {
                    lat: data.location.lat,
                    lng: data.location.lng,
                    accuracy: data.location.accuracy ?? 0,
                    altitude: data.location.altitude ?? 0,
                    heading: data.location.heading ?? 0,
                    speed: data.location.speed ?? 0,
                    timestamp: new Date(data.timestamp),
                  },
                  lastLocationTime: new Date(data.timestamp).toISOString(),
                }),
                // Update status if provided
                ...(data.status !== undefined && {
                  status: data.status as Operator["status"],
                }),
                // Update tracking status if provided
                ...(data.isTracking !== undefined && {
                  isTracking: data.isTracking,
                }),
              }
            : op
        )
      );
    };

    socketService.onOperatorHeartbeat(handleHeartbeat);

    return () => {
      // Clean up event listeners
      socketService.removeAllListeners();
    };
  }, []); // Keep empty dependency array since we want this to run once per mount

  return { operators };
}

export function useBlockOperator({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { operatorId: string }>({
    mutationFn: async ({ operatorId }) => {
      const response = await axios.patch<void>(
        `${import.meta.env.VITE_API_URL}/api/v1/operators/${operatorId}/block`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["operators"] });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

export function useUnblockOperator({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { operatorId: string }>({
    mutationFn: async ({ operatorId }) => {
      const response = await axios.patch<void>(
        `${import.meta.env.VITE_API_URL}/api/v1/operators/${operatorId}/unblock`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["operators"] });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

export function useDeleteOperator({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { operatorId: string }>({
    mutationFn: async ({ operatorId }) => {
      const response = await axios.delete<void>(
        `${import.meta.env.VITE_API_URL}/api/v1/operators/${operatorId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["operators"] });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}
